/**
 * employeeOnboardingSteps — LBR2-C3 (2026-07-26)
 *
 * PURE step model + validators for the employee onboarding wizard. No React,
 * no network → unit-testable. The wizard component consumes these to drive
 * step navigation, per-step validation, and the final submit gate.
 */

export const KYC_DOC_TYPES = ['PAN', 'AADHAAR', 'PASSPORT', 'BANK', 'PHOTO', 'OTHER'] as const;
export type KycDocType = (typeof KYC_DOC_TYPES)[number];

export const ONBOARDING_STEPS = [
  { id: 'identity', label: 'Identity & Login' },
  { id: 'personal', label: 'Personal & KYC' },
  { id: 'bank', label: 'Bank & Address' },
  { id: 'vehicle', label: 'Vehicle' },
  { id: 'documents', label: 'Documents & Review' },
] as const;
export type OnboardingStepId = (typeof ONBOARDING_STEPS)[number]['id'];

export interface OnboardingForm {
  // identity / login
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  employeeCode: string;
  phone: string;
  designation: string;
  departmentId: string;
  dateOfJoining: string;
  salary: string;
  // personal / KYC
  dateOfBirth: string;
  gender: string;
  maritalStatus: string;
  bloodGroup: string;
  pan: string;
  aadhaarNumber: string;
  passportNumber: string;
  personalEmail: string;
  // bank + address
  bankName: string;
  bankAccountNo: string;
  currentAddress: string;
  permanentAddress: string;
  personToBeContacted: string;
  emergencyPhoneNumber: string;
  emergencyRelation: string;
  // vehicle
  vehicleRegNo: string;
  vehicleType: string;
  fuelType: string;
}

export const emptyOnboardingForm: OnboardingForm = {
  firstName: '', lastName: '', email: '', password: '', employeeCode: '',
  phone: '', designation: '', departmentId: '', dateOfJoining: '', salary: '',
  dateOfBirth: '', gender: '', maritalStatus: '', bloodGroup: '', pan: '',
  aadhaarNumber: '', passportNumber: '', personalEmail: '',
  bankName: '', bankAccountNo: '', currentAddress: '', permanentAddress: '',
  personToBeContacted: '', emergencyPhoneNumber: '', emergencyRelation: '',
  vehicleRegNo: '', vehicleType: '', fuelType: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

/** Per-step validation → list of human error strings (empty = valid). */
/**
 * 2026-08-24 — the wizard now also EDITS an existing employee, not only creates.
 * The one rule that differs is the password: on create it mints the login and is
 * mandatory; on edit a blank box means "leave the password alone", and demanding
 * one would force HR to reset a password every time they fixed a phone number.
 * A NON-blank password on edit is still held to the same 8-character rule.
 */
export type WizardMode = 'create' | 'edit';

export function validateStep(
  stepId: OnboardingStepId,
  form: OnboardingForm,
  mode: WizardMode = 'create',
): string[] {
  const errs: string[] = [];
  if (stepId === 'identity') {
    if (!form.firstName.trim()) errs.push('First name is required');
    if (!form.lastName.trim()) errs.push('Last name is required');
    if (!form.email.trim() || !EMAIL_RE.test(form.email.trim())) errs.push('A valid email is required');
    // 🔒 EIGHT, measured on the TRIMMED value — the same rule the server applies
    // (`passwordForStorage(...).length >= 8` in employee-onboarding.routes.ts,
    // employee-onboarding.ts, employee-login-switch.routes.ts and hr.routes.ts).
    // This said SIX, so the wizard happily walked HR through five steps and then
    // surfaced a raw zod 400 on submit.
    const pwd = form.password.trim();
    if (mode === 'create') {
      if (pwd.length < 8) errs.push('Password must be at least 8 characters');
    } else if (pwd.length > 0 && pwd.length < 8) {
      // edit: blank = no change; anything typed must still clear the server bar.
      errs.push('Password must be at least 8 characters');
    }
    // 🔒 NO ROLE CHECK. The department's `department_login_policy.defaultRoleName`
    // decides the role server-side; `roleId` is discarded. Requiring it here made
    // HR pick a role that was never granted.
    if (!form.departmentId.trim()) errs.push('A department is required — it decides the login role');
  }
  if (stepId === 'personal') {
    if (form.pan.trim() && !PAN_RE.test(form.pan.trim())) {
      errs.push('PAN must be 5 letters, 4 digits, 1 letter (e.g. ABCPK1234F)');
    }
  }
  // bank / vehicle / documents steps are optional enrichment — no hard rules.
  return errs;
}

export function isStepComplete(
  stepId: OnboardingStepId,
  form: OnboardingForm,
  mode: WizardMode = 'create',
): boolean {
  return validateStep(stepId, form, mode).length === 0;
}

/** The wizard can submit once the identity step is valid (rest is enrichment). */
export function canSubmit(form: OnboardingForm, mode: WizardMode = 'create'): boolean {
  return isStepComplete('identity', form, mode);
}

export function kycDocTypeLabel(t: KycDocType): string {
  switch (t) {
    case 'PAN': return 'PAN card';
    case 'AADHAAR': return 'Aadhaar card';
    case 'PASSPORT': return 'Passport';
    case 'BANK': return 'Bank proof';
    case 'PHOTO': return 'Photo';
    case 'OTHER': return 'Other document';
  }
}
